import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Settings as SettingsIcon, Server, Palette, CreditCard, Sliders, Save, RefreshCw, CheckCircle, XCircle
} from 'lucide-react';

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [mt5Status, setMt5Status] = useState(null);
  const [formData, setFormData] = useState({
    // MT5 Settings
    mt5_server: '',
    mt5_login: '',
    mt5_password: '',
    mt5_api_key: '',
    // Branding
    company_name: '',
    primary_color: '',
    secondary_color: '',
    logo_url: '',
    // Features
    enable_copy_trading: true,
    enable_ib_portal: true,
    // Trading
    default_leverage: 100,
    max_leverage: 500,
    // Payments
    payment_gateway: 'mock',
    payment_api_key: ''
  });

  const fetchSettings = async () => {
    try {
      const [settingsRes, statusRes] = await Promise.all([
        api.getSettings(),
        api.getMT5Status()
      ]);
      setSettings(settingsRes.data);
      setMt5Status(statusRes.data);
      setFormData({
        mt5_server: settingsRes.data.mt5_server || '',
        mt5_login: settingsRes.data.mt5_login || '',
        mt5_password: '',
        mt5_api_key: '',
        company_name: settingsRes.data.company_name || '',
        primary_color: settingsRes.data.primary_color || '#00E5FF',
        secondary_color: settingsRes.data.secondary_color || '#CCFF00',
        logo_url: settingsRes.data.logo_url || '',
        enable_copy_trading: settingsRes.data.enable_copy_trading ?? true,
        enable_ib_portal: settingsRes.data.enable_ib_portal ?? true,
        default_leverage: settingsRes.data.default_leverage || 100,
        max_leverage: settingsRes.data.max_leverage || 500,
        payment_gateway: settingsRes.data.payment_gateway || 'mock',
        payment_api_key: ''
      });
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const response = await api.testMT5Connection();
      if (response.data.status === 'connected') {
        toast.success('MT5 connection successful!');
        setMt5Status({ ...mt5Status, connected: true, error: null });
      } else {
        toast.error(response.data.message || 'Connection failed');
        setMt5Status({ ...mt5Status, connected: false, error: response.data.message });
      }
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Connection test failed');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async (section) => {
    setSaving(true);
    try {
      let updateData = {};
      
      switch (section) {
        case 'mt5':
          updateData = {
            mt5_server: formData.mt5_server,
            mt5_login: formData.mt5_login,
            ...(formData.mt5_password && { mt5_password: formData.mt5_password }),
            ...(formData.mt5_api_key && { mt5_api_key: formData.mt5_api_key })
          };
          break;
        case 'branding':
          updateData = {
            company_name: formData.company_name,
            primary_color: formData.primary_color,
            secondary_color: formData.secondary_color,
            logo_url: formData.logo_url
          };
          break;
        case 'features':
          updateData = {
            enable_copy_trading: formData.enable_copy_trading,
            enable_ib_portal: formData.enable_ib_portal,
            default_leverage: parseInt(formData.default_leverage),
            max_leverage: parseInt(formData.max_leverage)
          };
          break;
        case 'payments':
          updateData = {
            payment_gateway: formData.payment_gateway,
            ...(formData.payment_api_key && { payment_api_key: formData.payment_api_key })
          };
          break;
        default:
          return;
      }

      await api.updateSettings(updateData);
      toast.success('Settings saved successfully');
      fetchSettings();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required to modify settings');
      } else {
        toast.error('Failed to save settings');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Configure your CRM system settings</p>
      </div>

      {/* Settings Tabs */}
      <Tabs defaultValue="mt5" className="space-y-6">
        <TabsList className="bg-muted/50 p-1">
          <TabsTrigger value="mt5" className="gap-2" data-testid="tab-mt5">
            <Server className="w-4 h-4" />
            MT5 Connection
          </TabsTrigger>
          <TabsTrigger value="branding" className="gap-2" data-testid="tab-branding">
            <Palette className="w-4 h-4" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="features" className="gap-2" data-testid="tab-features">
            <Sliders className="w-4 h-4" />
            Features
          </TabsTrigger>
          <TabsTrigger value="payments" className="gap-2" data-testid="tab-payments">
            <CreditCard className="w-4 h-4" />
            Payments
          </TabsTrigger>
        </TabsList>

        {/* MT5 Settings */}
        <TabsContent value="mt5">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="w-5 h-5" />
                    MT5 Server Configuration
                  </CardTitle>
                  <CardDescription>
                    Connect to your MetaTrader 5 server for real-time data sync
                  </CardDescription>
                </div>
                <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${
                  settings?.mt5_connected ? 'bg-emerald-500/10' : 'bg-amber-500/10'
                }`}>
                  {settings?.mt5_connected ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-emerald-400" />
                      <span className="text-xs text-emerald-400">Connected</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 text-amber-400" />
                      <span className="text-xs text-amber-400">Not Connected</span>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="mt5_login">Manager Login</Label>
                  <Input
                    id="mt5_login"
                    value={formData.mt5_login}
                    onChange={(e) => setFormData({ ...formData, mt5_login: e.target.value })}
                    placeholder="Enter manager login (e.g., 1035)"
                    data-testid="mt5-login-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mt5_api_key">Web API Password</Label>
                  <Input
                    id="mt5_api_key"
                    type="password"
                    value={formData.mt5_api_key}
                    onChange={(e) => setFormData({ ...formData, mt5_api_key: e.target.value })}
                    placeholder="Enter Web API password"
                    data-testid="mt5-api-key-input"
                  />
                </div>
              </div>
              
              {/* Connection Status */}
              {mt5Status && (
                <div className={`p-4 rounded-lg border ${
                  mt5Status.connected 
                    ? 'bg-emerald-500/10 border-emerald-500/30' 
                    : 'bg-muted/50 border-border'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        {mt5Status.connected ? 'Connected to MT5 Server' : 'Connection Status'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {mt5Status.connected 
                          ? `Server: ${mt5Status.server} | Login: ${mt5Status.login}`
                          : mt5Status.error || 'Not connected. Save settings and test connection.'
                        }
                      </p>
                      {mt5Status.api_type && (
                        <p className="text-xs text-primary/80 mt-1">
                          Connection via: {mt5Status.api_type}
                        </p>
                      )}
                      {mt5Status.last_connection && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Last attempt: {new Date(mt5Status.last_connection).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <Button 
                      variant={mt5Status.connected ? "outline" : "default"}
                      onClick={handleTestConnection}
                      disabled={testingConnection || !formData.mt5_server || !formData.mt5_login}
                      data-testid="test-connection-btn"
                    >
                      {testingConnection ? (
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      ) : mt5Status.connected ? (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      ) : (
                        <Server className="w-4 h-4 mr-2" />
                      )}
                      {testingConnection ? 'Testing...' : 'Test Connection'}
                    </Button>
                  </div>
                </div>
              )}

              <div className="bg-blue-500/10 border border-blue-500/30 p-4 rounded-lg">
                <p className="text-sm text-blue-200">
                  <strong>TD Capital Web API Integration:</strong> This CRM connects directly to TD Capital's 
                  MT5 Web API for real-time trading data.
                </p>
                <ul className="text-sm text-blue-200/80 mt-2 list-disc list-inside space-y-1">
                  <li>Enter your Manager Login (e.g., 1035)</li>
                  <li>Enter your Web API Password (from broker portal)</li>
                  <li>Click "Test Connection" to verify access</li>
                </ul>
              </div>

              <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 rounded-lg">
                <p className="text-sm text-emerald-200">
                  <strong>Live Data Access:</strong> When connected, you'll have access to:
                </p>
                <ul className="text-sm text-emerald-200/80 mt-2 list-disc list-inside space-y-1">
                  <li>Real-time market prices (EURUSD, GBPUSD, etc.)</li>
                  <li>Live account balance and equity</li>
                  <li>Open positions and trade history</li>
                </ul>
              </div>

              <div className="flex justify-end gap-3">
                <Button onClick={() => handleSave('mt5')} disabled={saving} data-testid="save-mt5-btn">
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save MT5 Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Settings */}
        <TabsContent value="branding">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5" />
                White-Label Branding
              </CardTitle>
              <CardDescription>
                Customize the CRM appearance for your brand
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                  placeholder="Your Company Name"
                  data-testid="company-name-input"
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="primary_color">Primary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="primary_color"
                      value={formData.primary_color}
                      onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                      placeholder="#00E5FF"
                      data-testid="primary-color-input"
                    />
                    <div 
                      className="w-10 h-10 rounded-lg border border-border"
                      style={{ backgroundColor: formData.primary_color }}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secondary_color">Accent Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="secondary_color"
                      value={formData.secondary_color}
                      onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                      placeholder="#CCFF00"
                      data-testid="secondary-color-input"
                    />
                    <div 
                      className="w-10 h-10 rounded-lg border border-border"
                      style={{ backgroundColor: formData.secondary_color }}
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="logo_url">Logo URL</Label>
                <Input
                  id="logo_url"
                  value={formData.logo_url}
                  onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                  placeholder="https://example.com/logo.png"
                  data-testid="logo-url-input"
                />
              </div>
              <div className="flex justify-end">
                <Button onClick={() => handleSave('branding')} disabled={saving} data-testid="save-branding-btn">
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Branding
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Features Settings */}
        <TabsContent value="features">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sliders className="w-5 h-5" />
                Feature Configuration
              </CardTitle>
              <CardDescription>
                Enable or disable CRM features and trading parameters
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div>
                    <p className="font-medium">Copy Trading Module</p>
                    <p className="text-sm text-muted-foreground">Allow traders to copy signals from providers</p>
                  </div>
                  <Switch
                    checked={formData.enable_copy_trading}
                    onCheckedChange={(checked) => setFormData({ ...formData, enable_copy_trading: checked })}
                    data-testid="copy-trading-switch"
                  />
                </div>
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div>
                    <p className="font-medium">IB/Affiliate Portal</p>
                    <p className="text-sm text-muted-foreground">Enable introducing broker dashboard</p>
                  </div>
                  <Switch
                    checked={formData.enable_ib_portal}
                    onCheckedChange={(checked) => setFormData({ ...formData, enable_ib_portal: checked })}
                    data-testid="ib-portal-switch"
                  />
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="default_leverage">Default Leverage</Label>
                  <Input
                    id="default_leverage"
                    type="number"
                    value={formData.default_leverage}
                    onChange={(e) => setFormData({ ...formData, default_leverage: e.target.value })}
                    min={1}
                    max={formData.max_leverage}
                    data-testid="default-leverage-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_leverage">Maximum Leverage</Label>
                  <Input
                    id="max_leverage"
                    type="number"
                    value={formData.max_leverage}
                    onChange={(e) => setFormData({ ...formData, max_leverage: e.target.value })}
                    min={1}
                    max={1000}
                    data-testid="max-leverage-input"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => handleSave('features')} disabled={saving} data-testid="save-features-btn">
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Features
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payments Settings */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payment Gateway
              </CardTitle>
              <CardDescription>
                Configure payment processing for deposits and withdrawals
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Payment Provider</Label>
                <div className="grid gap-3 sm:grid-cols-3">
                  {['mock', 'stripe', 'paypal'].map((gateway) => (
                    <div
                      key={gateway}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        formData.payment_gateway === gateway 
                          ? 'border-primary bg-primary/5' 
                          : 'border-border hover:border-muted-foreground'
                      }`}
                      onClick={() => setFormData({ ...formData, payment_gateway: gateway })}
                      data-testid={`gateway-${gateway}`}
                    >
                      <p className="font-medium capitalize">{gateway === 'mock' ? 'Mock (Demo)' : gateway}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {gateway === 'mock' && 'For testing purposes'}
                        {gateway === 'stripe' && 'Credit cards, ACH'}
                        {gateway === 'paypal' && 'PayPal payments'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {formData.payment_gateway !== 'mock' && (
                <div className="space-y-2">
                  <Label htmlFor="payment_api_key">API Key</Label>
                  <Input
                    id="payment_api_key"
                    type="password"
                    value={formData.payment_api_key}
                    onChange={(e) => setFormData({ ...formData, payment_api_key: e.target.value })}
                    placeholder="Enter API key"
                    data-testid="payment-api-key-input"
                  />
                </div>
              )}

              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Note:</strong> Payment integration is configured for Phase 2. Currently using mock 
                  payments for demonstration. Configure your payment gateway credentials when ready for production.
                </p>
              </div>
              <div className="flex justify-end">
                <Button onClick={() => handleSave('payments')} disabled={saving} data-testid="save-payments-btn">
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Payment Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
